import React, { useState } from 'react';
import {
  Typography,
  Form,
  InputNumber,
  Button,
  message,
  Row,
  Col,
  Image,
  Table,
} from 'antd';
import axios from 'axios';
import Logo from '../../assets/logo-saha.svg';
import './ScoreCalculate.scss'; // SCSS'i Register ile ortak kullanıyoruz

type Field = { label: string; key: string; count: number };

const TYT: Field[] = [
  { label: 'Türkçe (40)', key: 'turkce', count: 40 },
  { label: 'Sosyal Bilimler (20)', key: 'sosyal', count: 20 },
  { label: 'Temel Matematik (40)', key: 'temelMat', count: 40 },
  { label: 'Fen Bilimleri (20)', key: 'fen', count: 20 },
];

const AYT_GROUPS: { title: string; rows: Field[] }[] = [
  {
    title: 'TDE / Sosyal Bilimler 1',
    rows: [
      { label: 'Türk Dili ve Edebiyatı (24)', key: 'edebiyat', count: 24 },
      { label: 'Tarih‑1 (10)', key: 'tarih1', count: 10 },
      { label: 'Coğrafya‑1 (6)', key: 'cog1', count: 6 },
    ],
  },
  {
    title: 'Sosyal Bilimler 2',
    rows: [
      { label: 'Tarih‑2 (11)', key: 'tarih2', count: 11 },
      { label: 'Coğrafya‑2 (11)', key: 'cog2', count: 11 },
      { label: 'Felsefe Grubu (12)', key: 'felsefe', count: 12 },
      { label: 'Din Kültürü (6)', key: 'din', count: 6 },
    ],
  },
  {
    title: 'Matematik',
    rows: [{ label: 'Matematik (40)', key: 'aytMat', count: 40 }],
  },
  {
    title: 'Fen Bilimleri',
    rows: [
      { label: 'Fizik (14)', key: 'fizik', count: 14 },
      { label: 'Kimya (13)', key: 'kimya', count: 13 },
      { label: 'Biyoloji (13)', key: 'biyoloji', count: 13 },
    ],
  },
];

const YDT: Field = { label: 'Dil (80)', key: 'dil', count: 80 };
const ALL_FIELDS: Field[] = [
  ...TYT,
  ...AYT_GROUPS.flatMap((g) => g.rows),
  YDT,
];

export default function ScoreCalculate() {
  const [form] = Form.useForm();
  const [diploma, setDiploma] = useState<number>(75);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any[] | null>(null);

  // Her alan için net state’i
  const [nets, setNets] = useState<Record<string, number>>(
    Object.fromEntries(ALL_FIELDS.map(f => [f.key, 0]))
  );

  // Net hesaplama fonksiyonu
  const net = (d: number, y: number) => Math.max(0, +(d - y / 4).toFixed(2));

  // Doğru/yanlış değişikliğinde net ve max değer kontrolü
  const handleInputChange = (key: string, type: 'd' | 'y', value: number) => {
    const field = ALL_FIELDS.find(f => f.key === key)!;
    const otherType = type === 'd' ? 'y' : 'd';

    let d = form.getFieldValue(`${key}_d`) || 0;
    let y = form.getFieldValue(`${key}_y`) || 0;

    if (type === 'd') d = value;
    else y = value;

    // Doğru + yanlış > max ise, aşan değeri sıfırla!
    if (d + y > field.count) {
      if (type === 'd') d = field.count - y;
      else y = field.count - d;
      form.setFieldsValue({ [`${key}_${type}`]: type === 'd' ? d : y });
    }

    setNets(prev => ({ ...prev, [key]: net(d, y) }));
  };

  // Payload oluşturucu
  const buildPayload = (vals: any) => [
    ...ALL_FIELDS.map(f =>
      net(vals[`${f.key}_d`] || 0, vals[`${f.key}_y`] || 0).toFixed(2)
    ),
    diploma.toFixed(2).replace('.', ','),
  ];

  // Hesapla butonu
  const onFinish = async (vals: any) => {
    if (diploma < 50 || diploma > 100) {
      message.error('Diploma notu 50‑100 aralığında olmalıdır.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/score/calculate`,
        buildPayload(vals),
        { headers: { 'Content-Type': 'application/json' } }
      );
      setResult(data as any[]);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } catch (e: any) {
      message.error(e?.message || 'Hata');
    } finally {
      setLoading(false);
    }
  };

  // Form row - InputNumber ile
  const renderRow = (f: Field) => {
    const dKey = `${f.key}_d`;
    const yKey = `${f.key}_y`;
    return (
      <tr key={f.key}>
        <td style={{ width: 170 }}>{f.label}</td>
        <td style={{ textAlign: 'center' }}>
          <Form.Item name={dKey} noStyle initialValue={0}>
            <InputNumber
              min={0}
              max={f.count}
              style={{ width: 60 }}
              onChange={val => handleInputChange(f.key, 'd', Number(val) || 0)}
            />
          </Form.Item>
        </td>
        <td style={{ textAlign: 'center' }}>
          <Form.Item name={yKey} noStyle initialValue={0}>
            <InputNumber
              min={0}
              max={f.count}
              style={{ width: 60 }}
              onChange={val => handleInputChange(f.key, 'y', Number(val) || 0)}
            />
          </Form.Item>
        </td>
        <td style={{ textAlign: 'center', width: 60 }}>
          {nets[f.key] || 0}
        </td>
      </tr>
    );
  };

  // Sonuç tabloları
  const renderResultTables = () => {
    if (!result) return null;

    const tytHam = result[0];
    const aytHam = result[1];
    const ydtHam = result[2];
    const tytYer = result[3];
    const aytYer = result[4];
    const ydtYer = result[5];

    const makeTable = (header: string, data: { label: string; ham: any; yer: any }[]) => (
      <Table
        className='score-calc-result-table'
        bordered
        title={() => <strong>{header}</strong>}
        pagination={false}
        columns={[
          {
            title: '',
            dataIndex: 'label',
            render: (text: string) => <strong>{text}</strong>,
          }, { title: <h3 style={{ padding: 0, margin: 0 }}>Ham</h3>, dataIndex: 'ham' },
          { title: <h3 style={{ padding: 0, margin: 0 }}>Yerleştirme</h3>, dataIndex: 'yer' },
        ]}
        dataSource={data}
        rowKey="label"
        size="small"
        style={{ marginBottom: 16 }}
      />
    );

    return (
      <div>
        <div style={{ color: "#a3aed8", textAlign: "center", marginBottom: 16 }}>
          <Image width={200} preview={false} src={Logo}></Image>
        </div>
        {makeTable('TYT', [
          {
            label: 'TYT Puanı',
            ham: tytHam.tytPuani?.toFixed(3),
            yer: tytYer.tytPuani?.toFixed(3),
          },
          {
            label: 'TYT Sıra Aralığı',
            ham: tytHam.tytAralik || '-',
            yer: tytYer.tytAralik || '-',
          },
        ])}
        {makeTable('AYT', [
          {
            label: 'Eşit Ağırlık Puanı',
            ham: aytHam.esitAgirlikPuani?.toFixed(3),
            yer: aytYer.esitAgirlikPuani?.toFixed(3),
          },
          {
            label: 'Eşit Ağırlık Sıra Aralığı',
            ham: aytHam.eaAralik || '-',
            yer: aytYer.eaAralik || '-',
          },
          {
            label: '',
            ham: "",
            yer: "",
          },
          {
            label: 'Sözel Puanı',
            ham: aytHam.sozelPuani?.toFixed(3),
            yer: aytYer.sozelPuani?.toFixed(3),
          },

          {
            label: 'Sözel Sıra Aralığı',
            ham: aytHam.sozAralik || '-',
            yer: aytYer.sozAralik || '-',
          },
          {
            label: '',
            ham: "",
            yer: "",
          },
          {
            label: 'Sayısal Puanı',
            ham: aytHam.sayisalPuani?.toFixed(3),
            yer: aytYer.sayisalPuani?.toFixed(3),
          },
          {
            label: 'Sayısal Sıra Aralığı',
            ham: aytHam.sayAralik || '-',
            yer: aytYer.sayAralik || '-',
          },
        ])}
        {makeTable('YABANCI DİL', [
          {
            label: 'Dil Puanı',
            ham: ydtHam.dilPuani?.toFixed(3),
            yer: ydtYer.dilPuani?.toFixed(3),
          },
          {
            label: 'Sıra Aralığı',
            ham: ydtHam.dilAralik || '-',
            yer: ydtYer.dilAralik || '-',
          },
        ])}
      </div>
    );
  };

  return (
    <div
      style={{
        background: 'linear-gradient(120deg, #f7fafd 70%, #e7f0fc 100%)',
        padding: 0,
        overflow: 'auto',
      }}
    >
      <div
        style={{
          maxWidth: 1320,
          margin: '0 auto',
          padding: '24px 8px 24px 8px',
        }}
      >
        <Row gutter={[32, 32]}>
          <Col span={24}>
            <h1 style={{ textAlign: 'center', color: '#723fe7' }}>YKS Puan Hesaplama Motoru
            </h1>
          </Col>
          <Col xs={24} md={13} lg={14}>
            <div
              style={{
                background: 'rgba(255,255,255,0.95)',
                borderRadius: 32,
                boxShadow: '0 6px 24px -12px #b3d1fc',
                padding: 24,
                minHeight: 650,
              }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                size="middle"
                scrollToFirstError
                initialValues={Object.fromEntries(
                  ALL_FIELDS.flatMap(f => [
                    [`${f.key}_d`, 0],
                    [`${f.key}_y`, 0]
                  ])
                )}
              >
                <h2 style={{ background: 'black', marginBottom: 0, padding: 10, borderRadius: 5, color: 'white' }}>
                  TYT
                </h2>
                <table style={{ width: '100%', marginBottom: 16 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 300 }}></th>
                      <th>Doğru</th>
                      <th>Yanlış</th>
                      <th>Net</th>
                    </tr>
                  </thead>
                  <tbody>{TYT.map(renderRow)}</tbody>
                </table>
                <h2 style={{ background: 'black', marginBottom: 0, padding: 10, borderRadius: 5, color: 'white' }}>
                  AYT
                </h2>
                {AYT_GROUPS.map((g) => (
                  <React.Fragment key={g.title}>
                    <h3 style={{ background: '#723fe7', marginBottom: 0, padding: 10, borderRadius: 5, color: 'white' }}>
                      {g.title}
                    </h3>
                    <table style={{ width: '100%', marginBottom: 6 }}>
                      <thead>
                        <tr>
                          <th style={{ width: 300 }}></th>
                          <th>Doğru</th>
                          <th>Yanlış</th>
                          <th>Net</th>
                        </tr>
                      </thead>
                      <tbody>{g.rows.map(renderRow)}</tbody>
                    </table>
                  </React.Fragment>
                ))}
                <h2 style={{ background: 'black', marginBottom: 0, padding: 10, borderRadius: 5, color: 'white' }}>
                  YDT (Dil)
                </h2>
                <table style={{ width: '100%', marginBottom: 16 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 300 }}></th>
                      <th>Doğru</th>
                      <th>Yanlış</th>
                      <th>Net</th>
                    </tr>
                  </thead>
                  <tbody>{renderRow(YDT)}</tbody>
                </table>
                <h2 style={{ background: 'black', marginBottom: 16, padding: 10, borderRadius: 5, color: 'white' }}>
                  Diploma Notu
                </h2>
                <InputNumber
                  min={50}
                  max={100}
                  style={{ width: 120 }}
                  value={diploma}
                  onChange={(v) => setDiploma(v || 70)}
                />
                <span style={{ fontSize: 13, marginLeft: 12, color: '#888' }}>
                  (50‑100 arası bir değer)
                </span>
                <div style={{ marginTop: 28, display: 'flex', gap: 14 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{ flex: 1, fontWeight: 600, background: "#723fe7", borderRadius: 10 }}
                    size="large"
                  >
                    Hesapla
                  </Button>
                  <Button
                    style={{ flex: 1, borderRadius: 10 }}
                    onClick={() => {
                      form.resetFields();
                      setResult(null);
                      setNets(Object.fromEntries(ALL_FIELDS.map(f => [f.key, 0])));
                    }}
                    size="large"
                  >
                    Temizle
                  </Button>
                </div>
              </Form>
            </div>
          </Col>
          <Col xs={24} md={11} lg={10}>
            <div
              style={{
                background: 'rgba(255,255,255,0.99)',
                borderRadius: 32,
                boxShadow: '0 6px 24px -12px #b3d1fc',
                padding: 24,
                minHeight: 650,
              }}
              id="result-section"
            >
              {renderResultTables() || (
                <div style={{ color: "#a3aed8", textAlign: "center", marginBottom: 16 }}>
                  <Image width={200} preview={false} src={Logo}></Image>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}
